import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Text, 
  HStack, 
  Checkbox, 
  IconButton, 
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Input,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Spinner,
} from '@chakra-ui/react';
import { ChevronDownIcon, EmailIcon, PhoneIcon, EditIcon, DeleteIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import { LuPencil, LuRefreshCw } from 'react-icons/lu';
import { FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { getColumnStyle, getMessageColumns, shouldShowSubjectColumn } from '../../../utils/tableUtils';
import { FiX } from 'react-icons/fi';

const LinkedInIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

/** Small inline Smartlead logo icon for table headers */
const SmartleadIcon = ({ size = "16px" }) => (
  <Box
    as="svg"
    viewBox="0 0 40 40"
    w={size}
    h={size}
    flexShrink={0}
  >
    <path d="M0 5.03C0 2.25 2.25 0 5.03 0H34.97C37.75 0 40 2.25 40 5.03V34.97C40 37.75 37.75 40 34.97 40H5.03C2.25 40 0 37.75 0 34.97V5.03Z" fill="#6E58F1"/>
    <path d="M5.77 16.92C5.77 13.47 8.56 10.67 12 10.67H12.35C13.81 10.67 14.99 11.86 14.99 13.32V20.53C14.99 21.98 13.81 23.17 12.35 23.17H12C8.56 23.17 5.77 20.37 5.77 16.92Z" fill="white"/>
    <path d="M16.41 11.78C16.41 11.11 16.8 10.5 17.41 10.23L26.47 6.15C27.59 5.65 28.85 6.47 28.85 7.7V26.27C28.85 27.52 27.57 28.34 26.45 27.82L17.39 23.63C16.79 23.35 16.41 22.75 16.41 22.08V21.22C16.41 20.52 16.84 19.88 17.5 19.63L22.55 17.72C23.28 17.44 23.28 16.41 22.55 16.13L17.5 14.22C16.84 13.97 16.41 13.33 16.41 12.63V11.78Z" fill="white"/>
    <path d="M34.22 18.41V18.03C34.22 15.94 32.53 14.24 30.45 14.24C30.34 14.24 30.26 14.33 30.26 14.43V22.01C30.26 22.11 30.34 22.2 30.45 22.2C32.53 22.2 34.22 20.5 34.22 18.41Z" fill="white"/>
    <path d="M12 25.58C12 25.21 12.29 24.91 12.66 24.91H16.17C16.54 24.91 16.84 25.21 16.84 25.58V31.57C16.84 32.91 15.76 34 14.42 34C13.08 34 12 32.91 12 31.57V25.58Z" fill="white"/>
  </Box>
);

/** Small inline HeyReach logo icon for table headers — exact copy from HeyreachExportModal */
const HeyReachIcon = ({ size = "16px" }) => (
  <Box
    as="svg"
    viewBox="0 0 210 154.62"
    w={size}
    h={size}
    flexShrink={0}
  >
    <defs>
      <linearGradient id="hr-hdr-g1" x1="36.23" y1="68.19" x2="79.75" y2="68.19" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#ff2832"/><stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="hr-hdr-g2" x1="12.65" y1="85.52" x2="84.19" y2="85.52" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#ffbc00"/><stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="hr-hdr-g3" x1="27.39" y1="99.25" x2="89.93" y2="99.25" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#78ba3a"/><stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="hr-hdr-g4" x1="6.87" y1="116.06" x2="97.46" y2="116.06" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#37b2ff"/><stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="hr-hdr-g5" x1="57.5" y1="126.2" x2="108.07" y2="126.2" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#388eff"/><stop offset="1" stopColor="#fff"/>
      </linearGradient>
    </defs>
    <g>
      <path d="M201.62,58.75l-.03-.29-.06-.29c-.76-3.93-4.59-7.1-9.5-7.89-.87-.14-1.76-.21-2.65-.21-1.91,0-3.81.31-5.65.93-4.43,1.49-8.48,4.55-12.35,9.36-1.12,1.38-2.21,2.87-3.24,4.45-3.97-7.64-10.95-23.73-12.6-44.94-.52-7.02-6.49-12.51-13.58-12.51-.17,0-.35,0-.52.01h-.07c-2.67.13-5.17,1.01-7.28,2.51-.79-2.44-2.2-4.66-4.14-6.37l-.07-.06c-2.59-2.23-5.87-3.45-9.23-3.45-.52,0-1.06.03-1.52.08-7.45.78-13.02,7.2-12.7,14.63.02.6.05,1.19.07,1.78-2.05-1.14-4.33-1.74-6.65-1.74-.96,0-1.92.1-2.82.29-7.34,1.48-12.19,8.45-11.04,15.88.42,2.67.88,5.28,1.34,7.7-1.38-.46-2.84-.7-4.35-.7-.95,0-1.91.1-2.82.29-6.79,1.41-11.51,7.71-10.98,14.67l.02.18c.08.77.18,1.38.28,1.92l-30.93,5.62c-4.97.91-8.72,5.05-9.14,10.1-.09,1.09-.02,2.17.2,3.17.27,1.31.77,2.53,1.46,3.63l-16.08,2.92c-5.82,1.05-9.82,6.47-9.1,12.35l.02.14c.73,5.53,5.49,9.7,11.07,9.7.61,0,1.22-.05,1.87-.16l1.91-.35c-.14.7-.22,1.42-.22,2.16,0,.6.04,1.18.13,1.73.24,1.52.79,2.92,1.57,4.17l-13.02,2.37c-2.99.53-5.58,2.22-7.3,4.76-1.7,2.53-2.3,5.56-1.67,8.53l.06.25c1.16,5.02,5.73,8.66,10.88,8.66.59,0,1.2-.05,1.85-.15h.08s37.74-6.88,37.74-6.88c-.08.39-.14.79-.18,1.19-.09,1.02-.04,2.05.16,3.06,1,5.28,5.61,9.11,10.98,9.11.61,0,1.22-.05,1.87-.16l42.19-7.68c1.34,1.23,2.66,2.33,3.93,3.31l.08.08.04.03.19.18.3.18,1.53,1.16h.21c8.61,5.58,18.21,8.52,27.89,8.52h0c4.55,0,9.1-.65,13.52-1.93,9.94-2.87,17.19-7.77,22.8-15.42,6.67-9.1,10.97-22.14,13.13-39.86.84-6.95,4.1-12.15,6.97-16.73,2.92-4.62,5.69-8.99,5.15-13.97Z"/>
      <path fill="#fff" d="M138.14,64.24c-6.78-15.89-9.02-30.66-9.69-40.9v-.13c-.23-2.92-.41-6.02-.54-9.29-.09-2.02-.99-3.91-2.51-5.25-1.57-1.35-3.54-1.97-5.56-1.74-3.82.4-6.73,3.72-6.55,7.54.85,27.34,9.42,48.48,11.71,53.69,4.18-1.84,8.62-3.1,13.15-3.9Z"/>
      <path fill="#fff" d="M194.79,59.48c-.13-.67-1.35-2.02-3.86-2.42-1.66-.27-3.41-.09-5.02.45-3.73,1.25-6.78,4.17-9.2,7.18-1.17,1.44-2.25,2.96-3.24,4.53-.4.67-.8,1.35-1.21,2.02-.49.9-.85,1.93-1.52,2.69-.54.72-1.39.99-2.2,1.31-.8.32-1.61.67-2.38,1.08-1.52.8-2.96,1.75-4.35,2.78-3.72,2.87-6.82,6.69-8.66,11.04-.9,3.19-5.3,2.16-4.58-1.03.63-2.29,1.66-4.44,2.83-6.5,2.83-4.99,7.05-9.16,11.99-12.16-3.95-7.05-12.75-25.14-14.68-50.05-.27-3.59-3.37-6.29-7-6.15-1.84.09-3.5.85-4.72,2.2-1.21,1.35-1.84,3.05-1.75,4.85.49,9.97,2.56,25.22,9.79,41.61.09.18.14.36.18.54,1.44-.09,2.87-.13,4.31-.13,2.91-.05,3.19,4.17.31,4.53-5.47.36-10.95,1.39-16.11,3.28-4.94,1.84-9.69,4.26-13.64,7.67-3.5,3.46-8.35-1.93-4.49-5.07,1.13-.86,2.29-1.66,3.46-2.38-1.35-2.91-5.03-11.53-8.03-24.01,0-.04-.05-.09-.05-.14-1.52-5.88-2.92-12.43-4.13-19.61-.36-1.93-1.39-3.55-3.01-4.67-1.61-1.12-3.5-1.52-5.43-1.12-3.77.76-6.2,4.31-5.61,8.08,1.57,9.97,3.73,19.03,4.14,20.7.04.04.04.09.04.13.05.09.05.14.05.14,1.88,6.87,4.71,16.07,8.61,25,.76,1.75-.04,3.72-1.75,4.49-1.74.76-3.72-.04-4.49-1.75-4.58-10.45-7.77-21.32-9.65-28.55-.94-3.59-4.53-5.83-8.17-5.07-3.45.71-5.79,3.94-5.52,7.41.13,1.22.33,1.91.51,2.79,4.31,20.74,14.53,58.5,37.72,76.12t.01,0s.03.02.05.03c11.36,7.99,24.65,10.5,37.35,6.82,8.53-2.47,14.46-6.46,19.17-12.88,5.92-8.08,9.83-20.06,11.85-36.63,1.03-8.48,4.89-14.63,7.99-19.57,2.47-3.91,4.4-7,4.13-9.56ZM168.71,103.65c-.31,2.74-.94,5.52-1.93,8.21-5.21,15.17-22.98,20.87-37.03,14.54-4.41-1.96-8.37-4.86-11.34-8.62-.97.99-1.93,1.99-2.85,3.01-2.92,3.01-7.27-1.48-4.08-4.31,4.04-3.23,8.21-6.1,12.61-8.75.85-.49,1.93-.22,2.42.63.45.76.27,1.71-.4,2.29-1.39,1.18-2.76,2.4-4.1,3.65,6.18,6,15.38,9.13,23.76,7.4,10.23-1.89,15.44-9.61,18.22-19.03.4-1.26,1.75-1.98,3.05-1.57,1.12.36,1.8,1.44,1.66,2.56Z"/>
      <path fill="url(#hr-hdr-g1)" d="M79.75,68.84l-38.43,7c-.25.04-.52.07-.78.07-2.03,0-3.8-1.44-4.21-3.43-.08-.38-.11-.8-.08-1.24.16-1.94,1.61-3.53,3.52-3.88l37.92-6.89c.6,2.63,1.28,5.44,2.06,8.37Z"/>
      <path fill="url(#hr-hdr-g2)" d="M84.19,83.56l-66.46,12.1c-.25.04-.52.07-.78.07-2.16,0-3.99-1.61-4.27-3.75v-.05c-.28-2.27,1.25-4.35,3.5-4.76l65.37-11.88c.8,2.7,1.68,5.47,2.64,8.26Z"/>
      <path fill="url(#hr-hdr-g3)" d="M89.93,98.06l-57.45,10.45c-.25.04-.52.07-.78.07-2.12,0-3.94-1.58-4.26-3.67-.03-.21-.05-.43-.05-.65,0-2.08,1.49-3.86,3.54-4.23l55.58-10.1c1.05,2.72,2.18,5.45,3.41,8.14Z"/>
      <path fill="url(#hr-hdr-g4)" d="M97.46,112.21l-85.49,15.56c-.26.04-.52.07-.78.07-1.99,0-3.74-1.39-4.19-3.33l-.03-.12c-.24-1.14,0-2.31.65-3.28.66-.98,1.66-1.63,2.81-1.83l82.52-15c1.41,2.72,2.91,5.37,4.52,7.93Z"/>
      <path fill="url(#hr-hdr-g5)" d="M108.07,125.81l-45.49,8.28c-.25.04-.52.07-.78.07-2.07,0-3.85-1.48-4.23-3.53-.08-.38-.1-.77-.06-1.15.17-1.93,1.61-3.52,3.52-3.87l40.55-7.37c2.01,2.69,4.17,5.23,6.48,7.57Z"/>
    </g>
  </Box>
);

/**
 * Table header for contact table
 * Displays column headers with select-all checkbox
 * 
 * @param {Object} props
 * @param {Object} props.columns - Column configuration object
 * @param {number} props.selectedCount - Number of selected rows
 * @param {number} props.totalCount - Total number of rows
 * @param {Function} props.onSelectAll - Select all handler
 * @param {Array} props.fieldDefinitions - Custom field definitions
 * @param {number} props.minWidth - Minimum width for the header
 * @param {Function} props.onContinueGeneration - Handler for continue generation button click
 * @param {boolean} props.isStatusBarVisible - Whether the status bar is currently visible
 * @param {Array} props.messageDrafts - Message drafts for subject/body columns
 * @param {Function} props.onMessageDraftEdit - Handler for editing a message draft
 * @param {Function} props.onMessageDraftRename - Handler for renaming a message draft
 * @param {Function} props.onMessageDraftDelete - Handler for deleting a message draft
 * @param {Function} props.onMessageDraftDescriptionUpdate - Handler for updating a message draft's description
 * @param {boolean} props.isSelectingAll - Shows mini loader while selecting all rows
 */
const ContactTableHeader = ({ 
  columns, 
  selectedCount, 
  totalCount, 
  onSelectAll, 
  fieldDefinitions = [], 
  minWidth, 
  onContinueGeneration, 
  isStatusBarVisible = false, 
  messageDrafts = [],
  exportTracking = [],
  exportedGroupIdsSmartlead = new Set(),
  exportedGroupIdsHeyreach = new Set(),
  exportedGroupIdsGillyreach = new Set(),
  collapsedExportGroupIds = new Set(),
  onToggleExportGroup = null,
  onMessageDraftEdit,
  onMessageDraftRename,
  onMessageDraftDelete,
  onMessageDraftDescriptionUpdate,
  onMessageDraftContinueGeneration,
  generatingDraftId = null,
  onDeleteField,
  isSelectingAll = false,
}) => {
  const isAllSelected = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;
  const [hoveredField, setHoveredField] = useState(null);
  
  // State for inline rename editing
  const [renamingDraftId, setRenamingDraftId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef(null);
  
  // State for confirmation modals
  const [pendingRename, setPendingRename] = useState(null); // { draftId, newName }
  const [pendingDelete, setPendingDelete] = useState(null); // { draftId, emailName }
  const { isOpen: isRenameModalOpen, onOpen: onRenameModalOpen, onClose: onRenameModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  // State for custom-field column deletion
  const [pendingDeleteField, setPendingDeleteField] = useState(null);
  const { isOpen: isDeleteFieldModalOpen, onOpen: onDeleteFieldModalOpen, onClose: onDeleteFieldModalClose } = useDisclosure();
  
  // State for description editing
  const [descriptionDraftId, setDescriptionDraftId] = useState(null); // which draft is being described
  const [descriptionValue, setDescriptionValue] = useState('');
  const { isOpen: isDescModalOpen, onOpen: onDescModalOpen, onClose: onDescModalClose } = useDisclosure();
  
  // Get message column configurations
  const messageColumns = getMessageColumns(messageDrafts);
  
  // Focus input when rename mode is activated
  useEffect(() => {
    if (renamingDraftId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingDraftId]);
  
  // Handle starting rename
  const handleStartRename = (draft) => {
    setRenamingDraftId(draft.id);
    setRenameValue(draft.emailName);
  };
  
  // Handle rename input key press
  const handleRenameKeyDown = (e, draft) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (renameValue.trim() && renameValue.trim() !== draft.emailName) {
        setPendingRename({ draftId: draft.id, newName: renameValue.trim() });
        onRenameModalOpen();
      }
      setRenamingDraftId(null);
    } else if (e.key === 'Escape') {
      setRenamingDraftId(null);
      setRenameValue('');
    }
  };
  
  // Handle rename blur (click outside)
  const handleRenameBlur = () => {
    setRenamingDraftId(null);
    setRenameValue('');
  };
  
  // Confirm rename
  const handleConfirmRename = () => {
    if (pendingRename && onMessageDraftRename) {
      onMessageDraftRename(pendingRename.draftId, pendingRename.newName);
    }
    setPendingRename(null);
    onRenameModalClose();
  };
  
  // Handle delete click
  const handleDeleteClick = (draft) => {
    setPendingDelete({ draftId: draft.id, emailName: draft.emailName });
    onDeleteModalOpen();
  };
  
  // Confirm delete
  const handleConfirmDelete = () => {
    if (pendingDelete && onMessageDraftDelete) {
      onMessageDraftDelete(pendingDelete.draftId);
    }
    setPendingDelete(null);
    onDeleteModalClose();
  };
  
  // Handle opening description modal
  const handleEditDescription = (draft) => {
    setDescriptionDraftId(draft.id);
    setDescriptionValue(draft.description || '');
    onDescModalOpen();
  };
  
  // Save description
  const handleSaveDescription = () => {
    if (descriptionDraftId && onMessageDraftDescriptionUpdate) {
      onMessageDraftDescriptionUpdate(descriptionDraftId, descriptionValue.trim());
    }
    setDescriptionDraftId(null);
    setDescriptionValue('');
    onDescModalClose();
  };
  
  // Cancel description editing
  const handleCancelDescription = () => {
    setDescriptionDraftId(null);
    setDescriptionValue('');
    onDescModalClose();
  };

  // Handle custom field column delete
  const handleFieldDeleteClick = (e, fieldDef) => {
    e.stopPropagation();
    setPendingDeleteField(fieldDef);
    onDeleteFieldModalOpen();
  };

  const handleConfirmFieldDelete = () => {
    if (pendingDeleteField && onDeleteField) {
      onDeleteField(pendingDeleteField);
    }
    setPendingDeleteField(null);
    onDeleteFieldModalClose();
  };

  return (
    <Box
      position="sticky"
      top={0}
      zIndex={10}
      bg="#0a0a0a"
      border="1px solid"
      borderColor="rgba(255,255,255,0.08)"
      minH="50px"
      minW={minWidth ? `${minWidth}px` : "100%"}
      w="100%"
    >
      <HStack spacing={0} minH="50px" align="stretch" w="100%" minW="100%">
        <Box {...getColumnStyle(columns.checkbox)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" justifyContent="center" py={2}>
          {isSelectingAll ? (
            <Spinner size="xs" color="rgba(255,255,255,0.4)" thickness="2px" />
          ) : (
            <Checkbox
              isChecked={isAllSelected}
              isIndeterminate={isIndeterminate}
              onChange={(e) => onSelectAll(e.target.checked)}
              colorScheme="whiteAlpha"
            />
          )}
        </Box>
        <Box {...getColumnStyle(columns.title)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Title</Text>
        </Box>
        <Box {...getColumnStyle(columns.company)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Company</Text>
        </Box>
        <Box {...getColumnStyle(columns.name)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Name</Text>
        </Box>
        <Box {...getColumnStyle(columns.linkedin)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <HStack spacing={1.5}>
            <Box as={LinkedInIcon} boxSize={3} color="#0077B5" flexShrink={0} />
            <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">LinkedIn</Text>
          </HStack>
        </Box>
        <Box {...getColumnStyle(columns.email)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <HStack spacing={1.5}>
            <EmailIcon boxSize={3} color="blue.400" flexShrink={0} />
            <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">Email</Text>
          </HStack>
        </Box>
        <Box {...getColumnStyle(columns.phone)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <HStack spacing={1.5}>
            <PhoneIcon boxSize={3} color="blue.400" flexShrink={0} />
            <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">Phone</Text>
          </HStack>
        </Box>
        <Box {...getColumnStyle(columns.status)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Status</Text>
        </Box>
        <Box {...getColumnStyle(columns.notes)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Notes</Text>
        </Box>
        {fieldDefinitions.filter(f => f.source !== 'tracking').map((fieldDef, idx) => (
          <Box
            key={`contact_custom_header_${idx}`}
            {...getColumnStyle(columns[`custom_${idx}`])}
            px={4}
            borderRight="1px solid"
            borderColor="rgba(255,255,255,0.08)"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            py={2}
            position="relative"
            onMouseEnter={() => setHoveredField(fieldDef.field_id)}
            onMouseLeave={() => setHoveredField(null)}
          >
            <Text
              fontWeight="600"
              color="rgba(255,255,255,0.7)"
              fontSize="xs"
              wordBreak="break-word"
              whiteSpace="normal"
              lineHeight="1.4"
              textTransform="uppercase"
              letterSpacing="0.05em"
              flex={1}
              minW={0}
            >
              {fieldDef.display_name}
            </Text>
            {hoveredField === fieldDef.field_id && (
              <HStack spacing={0.5} flexShrink={0}>
                {onContinueGeneration && !isStatusBarVisible && (
                  <Tooltip label="Continue generating values for this field" placement="top" hasArrow>
                    <IconButton
                      icon={<ChevronDownIcon boxSize="2.5" />}
                      size="xs"
                      w="18px"
                      h="18px"
                      minW="18px"
                      p={0}
                      variant="ghost"
                      aria-label="Continue generation"
                      color="rgba(255,255,255,0.5)"
                      bg="rgba(255,255,255,0.08)"
                      _hover={{ bg: "rgba(255,255,255,0.15)", color: "#fafafa" }}
                      _focus={{ boxShadow: "none" }}
                      onClick={() => onContinueGeneration(fieldDef)}
                    />
                  </Tooltip>
                )}
                {onDeleteField && (
                  <Tooltip label="Delete column" placement="top" hasArrow>
                    <IconButton
                      icon={<DeleteIcon boxSize="2.5" />}
                      size="xs"
                      w="18px"
                      h="18px"
                      minW="18px"
                      p={0}
                      variant="ghost"
                      aria-label="Delete field"
                      color="rgba(239,68,68,0.7)"
                      _hover={{ bg: "rgba(239,68,68,0.1)", color: "rgba(239,68,68,0.9)" }}
                      _focus={{ boxShadow: "none" }}
                      onClick={(e) => handleFieldDeleteClick(e, fieldDef)}
                    />
                  </Tooltip>
                )}
              </HStack>
            )}
          </Box>
        ))}
        {/* Message Draft Columns - Subject and Body for each draft */}
        {messageDrafts.map((draft, idx) => (
          <React.Fragment key={`message_header_${draft.id}`}>
            {(() => {
              const hasAnyExportForGroup =
                exportedGroupIdsSmartlead.has(draft.id) || exportedGroupIdsHeyreach.has(draft.id) || exportedGroupIdsGillyreach.has(draft.id);
              const isCollapsed = collapsedExportGroupIds.has(draft.id);
              return (
                <>
            {shouldShowSubjectColumn(draft) && (
              draft.isDraftPreview ? (
                /* Simplified non-interactive header for preview-mode drafts */
                <Box
                  {...getColumnStyle(messageColumns[`message_${idx}_subject`])}
                  px={4}
                  borderRight="1px solid"
                  borderColor="rgba(255,255,255,0.08)"
                  display="flex"
                  alignItems="center"
                  py={2}
                  opacity={0.75}
                >
                  <HStack spacing={2} flex={1} minW={0}>
                    <EmailIcon color="rgba(255,255,255,0.4)" boxSize={3} flexShrink={0} />
                    <Text
                      fontWeight="600"
                      color="rgba(255,255,255,0.7)"
                      fontSize="xs"
                      wordBreak="break-word"
                      whiteSpace="normal"
                      lineHeight="1.4"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                      flex={1}
                      minW={0}
                    >
                      {draft.emailName}: Subject
                    </Text>
                    {draft.draftStatus === 'generating' ? (
                      <Spinner size="xs" color="rgba(255,255,255,0.35)" flexShrink={0} />
                    ) : (
                      <Box
                        fontSize="10px"
                        fontWeight="600"
                        letterSpacing="0.04em"
                        color="rgba(251,191,36,0.9)"
                        bg="rgba(251,191,36,0.12)"
                        border="1px solid rgba(251,191,36,0.25)"
                        px={1.5}
                        py={0.5}
                        borderRadius="4px"
                        flexShrink={0}
                        lineHeight="1.4"
                        textTransform="uppercase"
                      >
                        preview
                      </Box>
                    )}
                  </HStack>
                </Box>
              ) : renamingDraftId === draft.id ? (
            /* Rename mode: plain Box — no Menu machinery so the Input receives all events freely */
            <Box
              {...getColumnStyle(messageColumns[`message_${idx}_subject`])}
              px={4}
              borderRight="1px solid"
              borderColor="rgba(255,255,255,0.08)"
              display="flex"
              alignItems="center"
              py={2}
            >
              <HStack spacing={2} flex={1} minW={0}>
                <EmailIcon color="rgba(255,255,255,0.4)" boxSize={3} flexShrink={0} />
                <Input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => handleRenameKeyDown(e, draft)}
                  onBlur={handleRenameBlur}
                  size="sm"
                  bg="#111111"
                  border="1px solid"
                  borderColor="rgba(255,255,255,0.35)"
                  color="#fafafa"
                  fontSize="sm"
                  fontWeight="600"
                  px={2}
                  h="24px"
                  _focus={{ borderColor: "rgba(255,255,255,0.5)", boxShadow: "none" }}
                />
              </HStack>
            </Box>
              ) : (
            <Menu isLazy>
              <MenuButton
                as={Box}
                {...getColumnStyle(messageColumns[`message_${idx}_subject`])}
                px={4}
                borderRight="1px solid"
                borderColor="rgba(255,255,255,0.08)"
                display="flex"
                alignItems="center"
                py={2}
                cursor="pointer"
                _hover={{ bg: "rgba(255,255,255,0.04)" }}
                transition="background 0.15s ease"
              >
                <HStack spacing={2} flex={1} minW={0}>
                  <EmailIcon color="rgba(255,255,255,0.4)" boxSize={3} flexShrink={0} />
                  {generatingDraftId === draft.id && (
                    <Spinner size="xs" color="rgba(255,255,255,0.4)" flexShrink={0} />
                  )}
                  {draft.description && (
                    <Tooltip
                      label={
                        <Box>
                          <Text fontWeight="600" fontSize="xs">{draft.emailName}: Subject</Text>
                          <Text fontSize="xs" color="rgba(255,255,255,0.6)" mt={1}>{draft.description}</Text>
                        </Box>
                      }
                      placement="top"
                      hasArrow
                      bg="#0f0f0f"
                      color="#fafafa"
                      border="1px solid rgba(255,255,255,0.1)"
                      px={3}
                      py={2}
                      borderRadius="6px"
                      shouldWrapChildren
                    >
                      <Box
                        as="span"
                        display="inline-flex"
                        flexShrink={0}
                        pointerEvents="auto"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <InfoOutlineIcon color="rgba(255,255,255,0.3)" boxSize={3} />
                      </Box>
                    </Tooltip>
                  )}
                  <Text
                    fontWeight="600"
                    color="rgba(255,255,255,0.7)"
                    fontSize="xs"
                    wordBreak="break-word"
                    whiteSpace="normal"
                    lineHeight="1.4"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    flex={1}
                    minW={0}
                  >
                    {draft.emailName}: Subject
                  </Text>
                  <ChevronDownIcon color="rgba(255,255,255,0.3)" boxSize={4} flexShrink={0} />
                </HStack>
              </MenuButton>
              <MenuList bg="#0f0f0f" borderColor="rgba(255,255,255,0.10)" minW="180px">
                <MenuItem
                  icon={<LuPencil />}
                  onClick={() => handleStartRename(draft)}
                  bg="#0f0f0f"
                  _hover={{ bg: "rgba(255,255,255,0.06)" }}
                  color="#fafafa"
                  fontSize="sm"
                >
                  Rename column
                </MenuItem>
                <MenuItem
                  icon={<InfoOutlineIcon />}
                  onClick={() => handleEditDescription(draft)}
                  bg="#0f0f0f"
                  _hover={{ bg: "rgba(255,255,255,0.06)" }}
                  color="#fafafa"
                  fontSize="sm"
                >
                  Edit description
                </MenuItem>
                <MenuItem
                  icon={<EditIcon />}
                  onClick={() => onMessageDraftEdit?.(draft)}
                  bg="#0f0f0f"
                  _hover={{ bg: "rgba(255,255,255,0.06)" }}
                  color="#fafafa"
                  fontSize="sm"
                >
                  Edit column
                </MenuItem>
                <MenuItem
                  icon={generatingDraftId === draft.id ? <Spinner size="xs" /> : <LuRefreshCw />}
                  isDisabled={!!generatingDraftId}
                  onClick={() => onMessageDraftContinueGeneration?.(draft)}
                  bg="#0f0f0f"
                  _hover={{ bg: "rgba(255,255,255,0.06)" }}
                  color="#fafafa"
                  fontSize="sm"
                >
                  Generate for new contacts
                </MenuItem>
                <MenuDivider borderColor="rgba(255,255,255,0.08)" />
                <MenuItem
                  icon={<DeleteIcon />}
                  onClick={() => handleDeleteClick(draft)}
                  bg="#0f0f0f"
                  _hover={{ bg: "rgba(239,68,68,0.08)" }}
                  color="rgba(239,68,68,0.8)"
                  fontSize="sm"
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Menu>
              )
            )}
            {/* Body Column */}
            {draft.isDraftPreview ? (
              /* Simplified non-interactive header for preview-mode drafts */
              <Box
                {...getColumnStyle(messageColumns[`message_${idx}_body`])}
                px={4}
                borderRight="1px solid"
                borderColor="rgba(255,255,255,0.08)"
                display="flex"
                alignItems="center"
                py={2}
                opacity={0.75}
              >
                <HStack spacing={2} flex={1} minW={0}>
                  <EmailIcon color="rgba(255,255,255,0.4)" boxSize={3} flexShrink={0} />
                  <Text
                    fontWeight="600"
                    color="rgba(255,255,255,0.7)"
                    fontSize="xs"
                    wordBreak="break-word"
                    whiteSpace="normal"
                    lineHeight="1.4"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    flex={1}
                    minW={0}
                  >
                    {draft.emailName}: Body
                  </Text>
                  {draft.draftStatus === 'generating' ? (
                    <Spinner size="xs" color="rgba(255,255,255,0.35)" flexShrink={0} />
                  ) : (
                    <Box
                      fontSize="10px"
                      fontWeight="600"
                      letterSpacing="0.04em"
                      color="rgba(251,191,36,0.9)"
                      bg="rgba(251,191,36,0.12)"
                      border="1px solid rgba(251,191,36,0.25)"
                      px={1.5}
                      py={0.5}
                      borderRadius="4px"
                      flexShrink={0}
                      lineHeight="1.4"
                      textTransform="uppercase"
                    >
                      preview
                    </Box>
                  )}
                </HStack>
              </Box>
            ) : renamingDraftId === draft.id ? (
            /* Rename mode: plain Box — no Menu machinery so the Input receives all events freely */
            <Box
              {...getColumnStyle(messageColumns[`message_${idx}_body`])}
              px={4}
              borderRight="1px solid"
              borderColor="rgba(255,255,255,0.08)"
              display="flex"
              alignItems="center"
              py={2}
              position="relative"
            >
              <HStack spacing={2} flex={1} minW={0}>
                <EmailIcon color="rgba(255,255,255,0.4)" boxSize={3} flexShrink={0} />
                <Input
                  ref={!shouldShowSubjectColumn(draft) ? renameInputRef : undefined}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => handleRenameKeyDown(e, draft)}
                  onBlur={handleRenameBlur}
                  size="sm"
                  bg="#111111"
                  border="1px solid"
                  borderColor="rgba(255,255,255,0.35)"
                  color="#fafafa"
                  fontSize="sm"
                  fontWeight="600"
                  px={2}
                  h="24px"
                  _focus={{ borderColor: "rgba(255,255,255,0.5)", boxShadow: "none" }}
                />
              </HStack>
            </Box>
            ) : (
            <Menu isLazy>
              <MenuButton
                as={Box}
                {...getColumnStyle(messageColumns[`message_${idx}_body`])}
                px={4}
                borderRight="1px solid"
                borderColor="rgba(255,255,255,0.08)"
                display="flex"
                alignItems="center"
                py={2}
                cursor="pointer"
                _hover={{ bg: "rgba(255,255,255,0.04)" }}
                transition="background 0.15s ease"
                position="relative"
              >
                <HStack spacing={2} flex={1} minW={0}>
                  <EmailIcon color="rgba(255,255,255,0.4)" boxSize={3} flexShrink={0} />
                  {generatingDraftId === draft.id && (
                    <Spinner size="xs" color="rgba(255,255,255,0.4)" flexShrink={0} />
                  )}
                  {draft.description && (
                    <Tooltip
                      label={
                        <Box>
                          <Text fontWeight="600" fontSize="xs">{draft.emailName}: Body</Text>
                          <Text fontSize="xs" color="rgba(255,255,255,0.6)" mt={1}>{draft.description}</Text>
                        </Box>
                      }
                      placement="top"
                      hasArrow
                      bg="#0f0f0f"
                      color="#fafafa"
                      border="1px solid rgba(255,255,255,0.1)"
                      px={3}
                      py={2}
                      borderRadius="6px"
                      shouldWrapChildren
                    >
                      <Box
                        as="span"
                        display="inline-flex"
                        flexShrink={0}
                        pointerEvents="auto"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <InfoOutlineIcon color="rgba(255,255,255,0.3)" boxSize={3} />
                      </Box>
                    </Tooltip>
                  )}
                  <Text
                    fontWeight="600"
                    color="rgba(255,255,255,0.7)"
                    fontSize="xs"
                    wordBreak="break-word"
                    whiteSpace="normal"
                    lineHeight="1.4"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    flex={1}
                    minW={0}
                  >
                    {draft.emailName}: Body
                  </Text>
                  <ChevronDownIcon color="rgba(255,255,255,0.3)" boxSize={4} flexShrink={0} />
                </HStack>
              </MenuButton>
              <MenuList bg="#0f0f0f" borderColor="rgba(255,255,255,0.10)" minW="180px">
                <MenuItem
                  icon={<LuPencil />}
                  onClick={() => handleStartRename(draft)}
                  bg="#0f0f0f"
                  _hover={{ bg: "rgba(255,255,255,0.06)" }}
                  color="#fafafa"
                  fontSize="sm"
                >
                  Rename column
                </MenuItem>
                <MenuItem
                  icon={<InfoOutlineIcon />}
                  onClick={() => handleEditDescription(draft)}
                  bg="#0f0f0f"
                  _hover={{ bg: "rgba(255,255,255,0.06)" }}
                  color="#fafafa"
                  fontSize="sm"
                >
                  Edit description
                </MenuItem>
                <MenuItem
                  icon={<EditIcon />}
                  onClick={() => onMessageDraftEdit?.(draft)}
                  bg="#0f0f0f"
                  _hover={{ bg: "rgba(255,255,255,0.06)" }}
                  color="#fafafa"
                  fontSize="sm"
                >
                  Edit column
                </MenuItem>
                <MenuItem
                  icon={generatingDraftId === draft.id ? <Spinner size="xs" /> : <LuRefreshCw />}
                  isDisabled={!!generatingDraftId}
                  onClick={() => onMessageDraftContinueGeneration?.(draft)}
                  bg="#0f0f0f"
                  _hover={{ bg: "rgba(255,255,255,0.06)" }}
                  color="#fafafa"
                  fontSize="sm"
                >
                  Generate for new contacts
                </MenuItem>
                <MenuDivider borderColor="rgba(255,255,255,0.08)" />
                <MenuItem
                  icon={<DeleteIcon />}
                  onClick={() => handleDeleteClick(draft)}
                  bg="#0f0f0f"
                  _hover={{ bg: "rgba(239,68,68,0.08)" }}
                  color="rgba(239,68,68,0.8)"
                  fontSize="sm"
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Menu>
            )}
            {/* Per-group export-column toggle - floats OUTSIDE body header so it doesn't clash */}
            {hasAnyExportForGroup && (
              <Box position="relative" w="0" h="0" overflow="visible">
                <Tooltip
                  label={isCollapsed ? "Show export columns for this message group" : "Hide export columns for this message group"}
                  placement="top"
                  hasArrow
                >
                  <IconButton
                    aria-label={isCollapsed ? "Show export columns for this message group" : "Hide export columns for this message group"}
                    icon={isCollapsed ? <FiChevronsLeft size={12} /> : <FiChevronsRight size={12} />}
                    size="xs"
                    w="20px"
                    h="20px"
                    minW="20px"
                    p={0}
                    borderRadius="5px"
                    variant="ghost"
                    color="gray.300"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.16)"
                    bg="rgba(255,255,255,0.04)"
                    backdropFilter="blur(4px)"
                    position="absolute"
                    top="15px"
                    right="-10px"
                    zIndex={20}
                    transition="all 240ms cubic-bezier(0.22, 1, 0.36, 1)"
                    _hover={{
                      color: "white",
                      bg: "rgba(99, 102, 241, 0.20)",
                      borderColor: "rgba(129, 140, 248, 0.50)",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleExportGroup?.(draft.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Box>
            )}
            {/* Smartlead Export Status Column — only when this group has Smartlead exports */}
            {!isCollapsed && exportedGroupIdsSmartlead.has(draft.id) && (
              <Box
                {...getColumnStyle(messageColumns[`message_${idx}_smartlead_exports`])}
                px={4}
                borderRight="1px solid"
                borderColor="rgba(255,255,255,0.08)"
                display="flex"
                alignItems="center"
                py={2}
                transition="all 220ms cubic-bezier(0.22, 1, 0.36, 1)"
              >
                <HStack spacing={1.5}>
                  <SmartleadIcon size="18px" />
                  <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">
                    Smartlead exports
                  </Text>
                </HStack>
              </Box>
            )}
            {/* HeyReach Export Status Column — only when this group has HeyReach exports */}
            {!isCollapsed && exportedGroupIdsHeyreach.has(draft.id) && (
              <Box
                {...getColumnStyle(messageColumns[`message_${idx}_heyreach_exports`])}
                px={4}
                borderRight="1px solid"
                borderColor="rgba(255,255,255,0.08)"
                display="flex"
                alignItems="center"
                py={2}
                transition="all 220ms cubic-bezier(0.22, 1, 0.36, 1)"
              >
                <HStack spacing={2}>
                  <HeyReachIcon size="25px" />
                  <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">
                    HeyReach exports
                  </Text>
                </HStack>
              </Box>
            )}
            {/* GillyReach Export Status Column */}
            {!isCollapsed && exportedGroupIdsGillyreach.has(draft.id) && (
              <Box
                {...getColumnStyle(messageColumns[`message_${idx}_gillyreach_exports`])}
                px={4}
                borderRight="1px solid"
                borderColor="rgba(255,255,255,0.08)"
                display="flex"
                alignItems="center"
                py={2}
                transition="all 220ms cubic-bezier(0.22, 1, 0.36, 1)"
              >
                <HStack spacing={1.5}>
                  <img src="/gillyreach-favicon.png" alt="GillyReach" style={{ width: 18, height: 18 }} />
                  <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">
                    GillyReach
                  </Text>
                </HStack>
              </Box>
            )}
                </>
              );
            })()}
          </React.Fragment>
        ))}
        <Box {...getColumnStyle(columns.location)} px={4} display="flex" alignItems="center" py={2}>
          <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Location</Text>
        </Box>
        {columns.address && (
          <Box {...getColumnStyle(columns.address)} px={4} display="flex" alignItems="center" py={2}>
            <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Address</Text>
          </Box>
        )}
      </HStack>
      
      {/* Rename Confirmation Modal */}
      <Modal isOpen={isRenameModalOpen} onClose={onRenameModalClose} isCentered size="sm">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0a0a0a" color="#fafafa" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px">
          <ModalHeader fontSize="lg" fontWeight="600" pb={3} borderBottomWidth="1px" borderColor="rgba(255,255,255,0.08)">Rename column</ModalHeader>
          <ModalBody>
            <Text color="#a3a3a3" fontSize="sm">
              Rename "{pendingRename?.newName ? messageDrafts.find(d => d.id === pendingRename?.draftId)?.emailName : ''}" to "{pendingRename?.newName}"?
              This will update both Subject and Body column names.
            </Text>
          </ModalBody>
          <ModalFooter gap={2} borderTopWidth="1px" borderColor="rgba(255,255,255,0.08)">
            <Button
              variant="ghost"
              onClick={onRenameModalClose}
              color="#a3a3a3"
              borderRadius="full"
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
            >
              Cancel
            </Button>
            <Button
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              _hover={{ opacity: 0.85 }}
              _focus={{ boxShadow: 'none' }}
              onClick={handleConfirmRename}
            >
              Rename
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose} isCentered size="sm">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0a0a0a" color="#fafafa" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px">
          <ModalHeader fontSize="lg" fontWeight="600" pb={3} borderBottomWidth="1px" borderColor="rgba(255,255,255,0.08)">Confirm Delete</ModalHeader>
          <ModalBody>
            <Text color="#a3a3a3" fontSize="sm">
              Are you sure you want to delete this message? This will delete all columns associated with it. You can't undo this.
            </Text>
          </ModalBody>
          <ModalFooter gap={2} borderTopWidth="1px" borderColor="rgba(255,255,255,0.08)">
            <Button
              variant="ghost"
              onClick={onDeleteModalClose}
              color="#a3a3a3"
              borderRadius="full"
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
            >
              Cancel
            </Button>
            <Button
              bg="rgba(239,68,68,0.15)"
              color="rgba(239,68,68,0.85)"
              border="1px solid rgba(239,68,68,0.25)"
              borderRadius="full"
              fontWeight="600"
              _hover={{ bg: 'rgba(239,68,68,0.25)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={handleConfirmDelete}
            >
              Delete message group
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Custom Field Confirmation Modal */}
      <Modal isOpen={isDeleteFieldModalOpen} onClose={onDeleteFieldModalClose} isCentered size="sm">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0a0a0a" color="#fafafa" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px">
          <ModalHeader fontSize="lg" fontWeight="600" pb={3} borderBottomWidth="1px" borderColor="rgba(255,255,255,0.08)">Delete column</ModalHeader>
          <ModalBody>
            <Text color="#a3a3a3" fontSize="sm">
              Are you sure you want to delete the <strong style={{ color: '#fafafa' }}>{pendingDeleteField?.display_name}</strong> column?
              All values will be removed. This cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter gap={2} borderTopWidth="1px" borderColor="rgba(255,255,255,0.08)">
            <Button
              variant="ghost"
              onClick={onDeleteFieldModalClose}
              color="#a3a3a3"
              borderRadius="full"
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
            >
              Cancel
            </Button>
            <Button
              bg="rgba(239,68,68,0.15)"
              color="rgba(239,68,68,0.85)"
              border="1px solid rgba(239,68,68,0.25)"
              borderRadius="full"
              fontWeight="600"
              _hover={{ bg: 'rgba(239,68,68,0.25)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={handleConfirmFieldDelete}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Description Modal */}
      <Modal isOpen={isDescModalOpen} onClose={handleCancelDescription} isCentered size="md">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0a0a0a" color="#fafafa" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px">
          <ModalHeader fontSize="lg" fontWeight="600" pb={3} borderBottomWidth="1px" borderColor="rgba(255,255,255,0.08)">
            <HStack justify="space-between" align="center" w="full">
              <Text>Edit column description</Text>
              <IconButton
                icon={<FiX />}
                onClick={handleCancelDescription}
                variant="ghost"
                color="#a3a3a3"
                fontSize="18px"
                size="sm"
                aria-label="Close"
                transition="all 0.2s ease"
                _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                _focus={{ boxShadow: 'none' }}
              />
            </HStack>
          </ModalHeader>
          <ModalBody pt={2} pb={4}>
            <Text fontSize="xs" color="#a3a3a3" mb={3}>
              Add a note for your own reference — describe the intent or context behind this message.
              This description applies to both Subject and Body columns in the
              "{messageDrafts.find(d => d.id === descriptionDraftId)?.emailName}" group.
            </Text>
            <Text fontSize="sm" fontWeight="500" color="rgba(255,255,255,0.6)" mb={2}>
              Column description
            </Text>
            <Textarea
              value={descriptionValue}
              onChange={(e) => setDescriptionValue(e.target.value)}
              placeholder="e.g. follow up 2 after follow up 1"
              size="sm"
              resize="vertical"
              minH="80px"
              bg="#111111"
              borderColor="rgba(255,255,255,0.12)"
              borderRadius="8px"
              color="#fafafa"
              _focus={{ borderColor: "rgba(255,255,255,0.35)", boxShadow: "none" }}
              _hover={{ borderColor: "rgba(255,255,255,0.2)" }}
              _placeholder={{ color: "rgba(255,255,255,0.25)" }}
            />
            <Text fontSize="xs" color="rgba(255,255,255,0.3)" mt={2}>
              This is for your internal records only and won't be visible to recipients.
            </Text>
          </ModalBody>
          <ModalFooter gap={2} pt={0} borderTopWidth="1px" borderColor="rgba(255,255,255,0.08)">
            <Button
              variant="ghost"
              onClick={handleCancelDescription}
              color="#a3a3a3"
              borderRadius="full"
              fontSize="sm"
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
              size="sm"
            >
              Cancel
            </Button>
            <Button
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              fontSize="sm"
              _hover={{ opacity: 0.85 }}
              _focus={{ boxShadow: 'none' }}
              onClick={handleSaveDescription}
              size="sm"
            >
              Save changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ContactTableHeader;

